import React, { useState, useCallback, useMemo } from 'react';
import { TextArea } from '@buoyhealth/common.buoy-input';

import SubmitButton from './SubmitButton';

import useTransition from '../hooks/useTransition';
import useIsPolaris from '../hooks/useIsPolaris';

import {
  QuestionType,
  type IPolarisInterviewQuestion,
} from 'src/packages/polaris/types/interviews';

const TextBoxInput = ({
  question,
  onQuestionSubmit,
}: {
  question?: IPolarisInterviewQuestion;
  onQuestionSubmit?: (questionIndex: number) => void;
}) => {
  const transition = useTransition();
  const isPolaris = useIsPolaris();

  const [value, setValue] = useState(question?.answer || '');
  const [isError, setIsError] = useState(!!question?.errorMessage);

  const isFreeTextSymptomInput = useMemo(
    () => question?.choice === QuestionType.FreeTextSymptom,
    [question?.choice],
  );

  // If a question loads with an errorMessage, this indicates user has already submitted their answer
  // and the response from the api was invalid. User can continue without changing the text
  // here, but it should be considered a "skip"
  const userHasNotChangedAnswer = useMemo(
    () => !!question?.errorMessage && value === question?.answer,
    [question?.answer, question?.errorMessage, value],
  );

  const submitButtonText = useMemo(() => {
    if (!isFreeTextSymptomInput) {
      return 'Submit';
    }
    if (userHasNotChangedAnswer) {
      return 'Skip';
    }
    return 'Next';
  }, [isFreeTextSymptomInput, userHasNotChangedAnswer]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsError(false);
    setValue(e.target.value as string);
  }, []);

  const handleSubmit = useCallback(() => {
    if (question?.index) {
      onQuestionSubmit?.(question.index);
    }

    transition({
      data: {
        value: userHasNotChangedAnswer ? '' : value,
        question,
        isPolaris,
      },
    });
  }, [question, transition, userHasNotChangedAnswer, value, isPolaris, onQuestionSubmit]);

  return (
    <>
      <TextArea
        InputProps={{
          sx: { backgroundColor: 'white' },
        }}
        fullWidth
        rows={5}
        multiline
        onChange={handleChange}
        value={value}
        placeholder={
          isFreeTextSymptomInput
            ? question?.placeholder
            : 'Add any further details for us to consider, e.g. unable to swallow large pills, sensitive skin, reason for use.'
        }
      />
      <SubmitButton
        onClick={handleSubmit}
        text={value.length ? submitButtonText : 'Skip'}
        isError={isError}
        errorMessage={question?.errorMessage}
      />
    </>
  );
};

export default TextBoxInput;
